/**
 * Sơ đồ kho Summary Bảo Lộc — bố cục bám Access `frmFactorySummarySubForm2`
 * (BL.A.* / BL.B.* / BL.C.*, Text952–956, Text1374/996/1380/1379/1000/999).
 */
import { Fragment, useMemo } from 'react'
import { Box, Paper, Typography } from '@mui/material'

import type { FactoryCell } from '@/store/api/factoryApi'

export type FactorySummaryMapCell = { field: string; label: string; value: FactoryCell }

function normalizeStockKey(key: string): string {
  return key.trim().toUpperCase().replace(/\s+/g, '')
}

/** Lookup tồn theo full_area_code (BL.A.E1, …) từ API hoặc sumRow. */
export function buildFactoryBlMapStockLookup(cells: FactorySummaryMapCell[]): Map<string, FactoryCell> {
  const m = new Map<string, FactoryCell>()
  for (const c of cells) {
    const candidates = new Set<string>()
    const field = String(c.field ?? '').trim()
    const label = String(c.label ?? '').trim()
    if (field) {
      candidates.add(normalizeStockKey(field))
      const bl = field.match(/BL\.[ABC]\.[A-Z0-9._]+/i)
      if (bl) candidates.add(normalizeStockKey(bl[0]))
    }
    if (label) {
      candidates.add(normalizeStockKey(label))
      const coord = label.match(/^([A-E])(\d(?:_\d)?)$/i)
      if (coord) {
        candidates.add(normalizeStockKey(`BL.A.${coord[1]}${coord[2]}`))
      }
      const bl2 = label.match(/BL\.[ABC]\.[A-Z0-9._]+/i)
      if (bl2) candidates.add(normalizeStockKey(bl2[0]))
    }
    for (const k of candidates) {
      if (k && !m.has(k)) m.set(k, c.value)
    }
  }
  return m
}

function getRowKeyCaseInsensitive(row: Record<string, FactoryCell> | null | undefined, normKey: string): string | undefined {
  if (!row) return undefined
  const want = normKey.toLowerCase()
  return Object.keys(row).find((k) => k.toLowerCase() === want)
}

function stringifyCell(v: FactoryCell): string {
  if (v === null || v === undefined) return ''
  return String(v)
}

function formatSummaryMetricValue(v: FactoryCell): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('en-US') : '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function hasQty(v: FactoryCell): boolean {
  return v !== null && v !== undefined && v !== '' && !(typeof v === 'number' && !Number.isFinite(v))
}

const KHO_A_ROWS = ['E', 'D', 'C', 'B', 'A'] as const
const KHO_A_COLS = ['1', '2', '3', '4', '5'] as const
const KHO_A_MERGE_SKIP = new Set(['D4', 'D5', 'C4', 'C5', 'B4', 'B5'])

/** Access: BL.B.* — 2 hàng × 3 cột (C/B/A). */
const KHO_B_ROWS: Array<{ rowLabel: string; areas: string[] }> = [
  { rowLabel: 'B', areas: ['BL.B.C1', 'BL.B.B1', 'BL.B.A1'] },
  { rowLabel: 'C', areas: ['BL.B.C2', 'BL.B.B2', 'BL.B.A2'] },
]

/** Access: BL.C.* — 5 hàng × 2 cột (B/A), nhãn hàng 5→1. */
const KHO_C_ROWS: Array<{ rowLabel: string; areas: string[] }> = [
  { rowLabel: '5', areas: ['BL.C.B5', 'BL.C.A5'] },
  { rowLabel: '4', areas: ['BL.C.B4', 'BL.C.A4'] },
  { rowLabel: '3', areas: ['BL.C.B3', 'BL.C.A3'] },
  { rowLabel: '2', areas: ['BL.C.B2', 'BL.C.A2'] },
  { rowLabel: '1', areas: ['BL.C.B1', 'BL.C.A1'] },
]

const LABEL_SX = 'PRODUCTION AREA'
const LABEL_LD = 'INCINERATOR & COMPRESSOR AREA'
const LABEL_PB = 'DUST ROOM'

const sectionTitleSx = {
  display: 'block',
  mb: 0.5,
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: 0.04,
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
}

const cellBorderSx = {
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  p: 0.25,
  minHeight: 34,
  textAlign: 'center' as const,
}

const allocationRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 1,
  py: 0.4,
  px: 0.75,
  borderRadius: 0.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(0,0,0,0.02)',
}

function WarehouseMiniGrid({
  colHeaders,
  rows,
  qty,
  maxWidth,
}: {
  colHeaders: string[]
  rows: Array<{ rowLabel: string; areas: string[] }>
  qty: (area: string) => FactoryCell
  maxWidth?: number | string
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `28px repeat(${colHeaders.length}, minmax(0, 1fr))`,
        gridTemplateRows: `20px repeat(${rows.length}, minmax(34px, auto))`,
        gap: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0.5,
        overflow: 'hidden',
        maxWidth: maxWidth ?? '100%',
      }}
    >
      <Box sx={{ ...cellBorderSx, borderTop: 0, borderLeft: 0, bgcolor: 'action.hover', minHeight: 20 }} />
      {colHeaders.map((h) => (
        <Box
          key={h}
          sx={{
            ...cellBorderSx,
            borderTop: 0,
            bgcolor: 'action.hover',
            minHeight: 20,
            fontSize: '0.62rem',
            fontWeight: 800,
          }}
        >
          {h}
        </Box>
      ))}
      {rows.map((row, ri) => (
        <Fragment key={row.rowLabel}>
          <Box
            sx={{
              ...cellBorderSx,
              borderLeft: 0,
              bgcolor: 'action.hover',
              fontSize: '0.62rem',
              fontWeight: 800,
              gridColumn: 1,
              gridRow: ri + 2,
            }}
          >
            {row.rowLabel}
          </Box>
          {row.areas.map((area, ci) => {
            const v = qty(area)
            const show = hasQty(v)
            const short = area.replace(/^BL\.[ABC]\./i, '')
            return (
              <Box
                key={area}
                sx={{
                  ...cellBorderSx,
                  gridColumn: ci + 2,
                  gridRow: ri + 2,
                }}
              >
                {show ? (
                  <>
                    <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.05, fontSize: '0.58rem' }}>
                      {short}
                    </Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.7rem' }}>
                      {formatSummaryMetricValue(v)}
                    </Typography>
                  </>
                ) : null}
              </Box>
            )
          })}
        </Fragment>
      ))}
    </Box>
  )
}

function AllocationLine({
  label,
  value,
}: {
  label: string
  value: FactoryCell
}) {
  return (
    <Box sx={allocationRowSx}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" fontWeight={800}>
        {formatSummaryMetricValue(value)}
      </Typography>
    </Box>
  )
}

/**
 * `scope` controls which parts of the dashboard map render:
 *  - 'all'        (default) — KHO A grid + KHO B + KHO C + PHÂN BỐ TỒN KHO
 *  - 'kho-a'      — chỉ render khối KHO A
 *  - 'kho-bc'     — chỉ render KHO B + KHO C
 *  - 'allocation' — chỉ render PHÂN BỐ TỒN KHO
 *
 * Bám layout Access `frmFactorySummarySubForm2` khi đặt cạnh mỗi section.
 */
/** Numeric coercion: trả về `null` cho ô không có giá trị thực (Access blank). */
function asNumber(v: FactoryCell): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

export function FactoryBaoLocSummaryMap({
  mapCells,
  sumRow,
  kvSx,
  scope = 'all',
  title,
  subtitle,
}: {
  mapCells: FactorySummaryMapCell[]
  sumRow?: Record<string, FactoryCell> | null
  kvSx?: number | null
  scope?: 'all' | 'kho-a' | 'kho-bc' | 'allocation'
  title?: string
  subtitle?: string
}) {
  const showKhoA = scope === 'all' || scope === 'kho-a'
  const showKhoBC = scope === 'all' || scope === 'kho-bc'
  const showAllocation = scope === 'all' || scope === 'allocation'
  const showMapPanel = showKhoA || showKhoBC
  const lookup = useMemo(() => buildFactoryBlMapStockLookup(mapCells), [mapCells])
  const qty = (area: string): FactoryCell => lookup.get(normalizeStockKey(area)) ?? null
  const hasMapData =
    lookup.size > 0 ||
    (typeof kvSx === 'number' && Number.isFinite(kvSx) && kvSx !== 0) ||
    Boolean(sumRow && Object.keys(sumRow).length > 0)

  const sxZone = qty('BL.A.LDKN')
  const pbZone = qty('BL.A.PB')
  const sxDisplay: FactoryCell =
    sxZone !== null && sxZone !== undefined && sxZone !== ''
      ? sxZone
      : typeof kvSx === 'number' && Number.isFinite(kvSx)
        ? kvSx
        : null

  // Allocation rows — compute từ chính mapCells (Mã khu vực thuộc sơ đồ kho
  // Bảo Lộc: BL.A.* / BL.B.* / BL.C.*). KV SX (production line WIP) cộng
  // chung vào tổng + Kho A theo Access spec (Label2237 group A includes WIP).
  const allocationRows = useMemo(() => {
    let za = 0, zb = 0, zc = 0, noArea = 0
    for (const c of mapCells) {
      const n = asNumber(c.value) ?? 0
      const f = (c.field || '').toUpperCase()
      if (f.startsWith('BL.A.')) za += n
      else if (f.startsWith('BL.B.')) zb += n
      else if (f.startsWith('BL.C.')) zc += n
      else noArea += n
    }
    const kv = asNumber(kvSx ?? null) ?? 0
    const total = za + zb + zc + noArea + kv
    return [
      { labelVi: 'Total (stack + WIP)', value: total },
      { labelVi: 'Zone A (BL.A.*) + WIP', value: za + kv },
      { labelVi: 'Zone B (BL.B.*)', value: zb },
      { labelVi: 'Zone C (BL.C.*)', value: zc },
      { labelVi: 'Unassigned area', value: noArea },
      { labelVi: 'WIP area', value: kv },
    ]
  }, [mapCells, kvSx])
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.02, mb: 1 }}>
        {title ?? 'WAREHOUSE MAP'}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '8fr 2fr' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
      {showMapPanel && !hasMapData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            No warehouse map data
          </Typography>
        </Box>
      ) : showMapPanel ? (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {showKhoA && (
        <>
        <Typography component="span" sx={sectionTitleSx}>
          ZONE A
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '28px repeat(5, minmax(0, 1fr)) 68px',
            gridTemplateRows: '20px repeat(5, minmax(34px, auto))',
            gap: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 0.5,
            overflow: 'hidden',
            mb: 1.25,
          }}
        >
          <Box sx={{ ...cellBorderSx, borderTop: 0, borderLeft: 0, bgcolor: 'action.hover', minHeight: 20 }} />
          {KHO_A_COLS.map((c) => (
            <Box
              key={`h-${c}`}
              sx={{
                ...cellBorderSx,
                borderTop: 0,
                bgcolor: 'action.hover',
                minHeight: 20,
                fontSize: '0.62rem',
                fontWeight: 800,
              }}
            >
              {c}
            </Box>
          ))}
          <Box sx={{ ...cellBorderSx, borderTop: 0, borderRight: 0, bgcolor: 'action.hover', minHeight: 20 }} />

          <Box
            sx={{
              ...cellBorderSx,
              gridColumn: '5 / 7',
              gridRow: '3 / 6',
              bgcolor: 'rgba(255, 235, 120, 0.55)',
              justifyContent: 'center',
              p: 0.4,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.1, textAlign: 'center', fontSize: '0.58rem' }}>
              {LABEL_SX}
            </Typography>
            <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.72rem' }}>
              {formatSummaryMetricValue(sxDisplay)}
            </Typography>
          </Box>

          <Box
            sx={{
              ...cellBorderSx,
              borderRight: 0,
              gridColumn: 7,
              gridRow: '2 / 4',
              bgcolor: 'rgba(255, 183, 77, 0.42)',
              px: 0.2,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.52rem', lineHeight: 1.05, textAlign: 'center' }}>
              {LABEL_LD}
            </Typography>
          </Box>

          <Box sx={{ ...cellBorderSx, borderRight: 0, gridColumn: 7, gridRow: 4, bgcolor: 'background.paper', minHeight: 30 }} />

          <Box
            sx={{
              ...cellBorderSx,
              borderRight: 0,
              gridColumn: 7,
              gridRow: 5,
              bgcolor: 'rgba(129, 199, 132, 0.45)',
              px: 0.2,
              zIndex: 1,
            }}
          >
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.52rem', lineHeight: 1.05, textAlign: 'center' }}>
              {LABEL_PB}
            </Typography>
            <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.65rem', lineHeight: 1.05 }}>
              {formatSummaryMetricValue(pbZone)}
            </Typography>
          </Box>

          <Box sx={{ ...cellBorderSx, borderRight: 0, gridColumn: 7, gridRow: 6, bgcolor: 'background.paper', minHeight: 30 }} />

          {KHO_A_ROWS.map((row, ri) => (
            <Fragment key={`row-${row}`}>
              <Box
                sx={{
                  ...cellBorderSx,
                  borderLeft: 0,
                  bgcolor: 'action.hover',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  gridColumn: 1,
                  gridRow: ri + 2,
                }}
              >
                {row}
              </Box>
              {KHO_A_COLS.map((col, ci) => {
                const short = `${row}${col}`
                if (KHO_A_MERGE_SKIP.has(short)) return null
                const area = `BL.A.${row}${col}`
                const v = qty(area)
                const show = hasQty(v)
                return (
                  <Box key={short} sx={{ ...cellBorderSx, gridColumn: ci + 2, gridRow: ri + 2 }}>
                    {show ? (
                      <>
                        <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.05, fontSize: '0.58rem' }}>
                          {short}
                        </Typography>
                        <Typography variant="caption" fontWeight={800} sx={{ lineHeight: 1.05, fontSize: '0.7rem' }}>
                          {formatSummaryMetricValue(v)}
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                )
              })}
            </Fragment>
          ))}
        </Box>
        </>
        )}

        {showKhoBC && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.25,
            alignItems: 'start',
          }}
        >
          <Box>
            <Typography component="span" sx={sectionTitleSx}>
              ZONE B
            </Typography>
            <WarehouseMiniGrid colHeaders={['C', 'B', 'A']} rows={KHO_B_ROWS} qty={qty} />
          </Box>
          <Box>
            <Typography component="span" sx={sectionTitleSx}>
              ZONE C
            </Typography>
            <WarehouseMiniGrid colHeaders={['B', 'A']} rows={KHO_C_ROWS} qty={qty} />
          </Box>
        </Box>
        )}
      </Box>
      ) : null}

      {showAllocation && (
      <Box
        sx={{
          minWidth: 0,
          pl: { xs: 0, md: 1 },
          borderLeft: { xs: 0, md: '1px dashed' },
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.02, mb: 0.75, fontSize: '0.78rem' }}>
          STOCK ALLOCATION
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {allocationRows.map((r) => (
            <AllocationLine key={r.labelVi} label={r.labelVi} value={r.value} />
          ))}
        </Box>
      </Box>
      )}
      </Box>
    </Paper>
  )
}
